import React, { Fragment, useState, useEffect, useRef } from "react";
import FlowSelector from "./FlowSelector";
import NodeList, { NODE_LIST_CONTROLLER } from "../NodeList/NodeList";
import { WORKSPACE_LEFT_LIST_ICONS } from "../v2/CustomisationWorkspace/WorkspaceAssets/Assets";
import { getSubNodes } from "../v2/CustomisationWorkspace/v2IntegrationHelpers/VisualEditorDataFormatHelperFunctions";
import { addVal, removeVal } from "../v2/CustomisationWorkspace/v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions";
import { useNavigate } from "react-router-dom";
import { RIGHT_CLICK_MENU_CONTROLLER } from "../RightClickMenu/RightClickMenu";
import { evaluateDisplayConditionsForListNode } from "../v2/CustomisationWorkspace/WorkspaceComponents/ModuleList/customFields/CustomFieldRenderer";
import { getIconCategoryClass, getIconCategoryColor } from "../common/iconCategory";
import LottieIcon from "../common/LottieIcon";

const logSelectionDebug = (stage, payload) => {
  console.log(`[SelectionDebug][LayersPanel] ${stage}`, payload);
};

const LayersPanel = ({ 
  layers, 
  expandedLayers, 
  onLayerToggle,
  isStackingMode,
  setIsStackingMode,
  stackingOrder,
  draggedStackItem,
  dragOverStackItem,
  onStackDragStart,
  onStackDragEnd,
  onStackDragOver,
  hoveredStackingItem,
  setHoveredStackingItem,
  isScrollingRef,
  flows,
  activeFlowId,
  onFlowChange,
  onCreateFlow,
  showExpansionLines,
  setShowExpansionLines,
  selectedFieldDataLink,

  leftListNewCustomisationItems,
  selectedFeatureId,
  selectedLayersNodeState,
  setSelectedFeatureId,
  setSelectedFeatureNode,
  setSelectedLayersNodeState,
  activeNode,
  activateNode,
  clearActiveNode,
  setTimelinePlayheadStep,
  forceUpdateComponentFunc,

  campaignData,
  libraryItems,
}) => {
  const navigate = useNavigate();
  const [persistedExpandedNodes, setPersistedExpandedNodes] = useState({});
  const previousSelectedFeatureIdRef = useRef(null);
  const suppressNextActiveNodeExpansionRef = useRef(false);

  const getNodeFeatureScope = (node) => (
    node?.topNode?.id
    || node?.visualisedTopNode?.id
    || node?.featureData?._id
    || node?.featureData?.id
    || node?.parent?.topNode?.id
    || (node?.depth === 0 ? node?.id : null)
    || null
  );

  const getNodeDataLink = (node) => String(
    node?.config?.groupSettings?.dataLink
    || node?.config?.dataLink
    || ''
  ).trim();

  const getNodeDataIndex = (node) => (
    Number.isInteger(node?.dataIndex) && node.dataIndex >= 0
      ? String(node.dataIndex)
      : ''
  );

  const getNodePathTail = (rawPath) => {
    const value = String(rawPath || '').trim();
    if (!value) return '';
    const parts = value.split('.').filter(Boolean);
    return parts.slice(-2).join('.');
  };

  const getNodeHierarchySegments = (node) => {
    const segments = [];
    let current = node;

    while (current && current.depth > 0) {
      segments.unshift({
        dataLink: getNodeDataLink(current),
        dataIndex: getNodeDataIndex(current),
        pathTail: getNodePathTail(current?.path),
        name: String(current?.name || '').trim().toLowerCase(),
        depth: Number.isInteger(current?.depth) ? String(current.depth) : '',
      });
      current = current.parent || null;
    }

    return segments;
  };

  const buildNodeSelectionToken = (node, fallbackFeatureId = null) => {
    const featureId = String(getNodeFeatureScope(node) || fallbackFeatureId || '').trim();

    if (!featureId) return null;

    return {
      featureId,
      hierarchy: getNodeHierarchySegments(node),
    };
  };

  const doesHierarchySegmentMatch = (leftSegment, rightSegment) => {
    if (!leftSegment || !rightSegment) return false;

    if (leftSegment.depth && rightSegment.depth && leftSegment.depth !== rightSegment.depth) {
      return false;
    }

    if (leftSegment.dataLink || rightSegment.dataLink) {
      if (!leftSegment.dataLink || !rightSegment.dataLink) {
        return false;
      }

      if (leftSegment.dataLink !== rightSegment.dataLink) {
        return false;
      }

      if ((leftSegment.dataIndex || rightSegment.dataIndex) && leftSegment.dataIndex !== rightSegment.dataIndex) {
        return false;
      }

      return true;
    }

    if (leftSegment.pathTail && rightSegment.pathTail && leftSegment.pathTail !== rightSegment.pathTail) {
      return false;
    }

    return Boolean(leftSegment.name && rightSegment.name && leftSegment.name === rightSegment.name);
  };

  const doesSelectionTokenMatchNode = (selectionToken, node) => {
    if (!selectionToken || !node) return false;

    const nodeFeatureId = String(getNodeFeatureScope(node) || '').trim();
    if (!nodeFeatureId || nodeFeatureId !== selectionToken.featureId) {
      return false;
    }

    const targetHierarchy = selectionToken.hierarchy || [];
    const nodeHierarchy = getNodeHierarchySegments(node);

    if (!targetHierarchy.length) {
      return node.depth === 0;
    }

    if (node.depth === 0) {
      return false;
    }

    if (targetHierarchy.length !== nodeHierarchy.length) {
      return false;
    }

    for (let i = 0; i < targetHierarchy.length; i++) {
      if (!doesHierarchySegmentMatch(targetHierarchy[i], nodeHierarchy[i])) {
        return false;
      }
    }

    return true;
  };
  const [rightClickedNode, setRightClickedNode] = useState(null);
    const nodeListRightClicked = (e, node, nodes, onDone) => {
        setRightClickedNode(node);
        // console.log("Right click on node: ", node);
        const rightClickMenu = [];

        if (node.removable) {
            rightClickMenu.push({
                label: "Delete " + node.name,
                action: (e) => {
                    // console.log("Delete clicked for new format node: ", node);
                    removeVal(node.valsArray, node.dataIndex, null, node);
                    RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
                    forceUpdateComponentFunc(node.parent);
                }
            });
        }
        if (rightClickMenu.length) {
            e.preventDefault();
            RIGHT_CLICK_MENU_CONTROLLER.showRightClickMenu(e, rightClickMenu, () => { NODE_LIST_CONTROLLER.refreshNodeList("customisation-workspace-left-list"); onDone(); });
            return true; // We handled the right click
        } else {
            RIGHT_CLICK_MENU_CONTROLLER.hideRightClickMenu();
            return false; // We did not handle the right click
        }
    }

  const getTriggeredComponents = () => {
    const componentList = [];

    // First build a list of all component ids in our list
    for (let i = 0; i < libraryItems.Campaign?.length; i++) {
      const component = libraryItems.Campaign[i];
      componentList.push(component);
    }

    // Now go through each flow and remove any components that appear in the flow steps
    for (let i = 0; i < flows.length; i++) {
      const flow = flows[i];
      for (let j = 0; j < flow.steps.length; j++) {
        const step = flow.steps[j];
        if (step.components) {
          for (let k = 0; k < step.components.length; k++) {
            const component = step.components[k];
            // do we have a matching component in our list?
            for (let m = 0; m < componentList.length; m++) {
              const listComponent = componentList[m];
              // console.log("Comparing flow component", component, "to list component", listComponent);
              if (listComponent.id === component.featureId) {
                componentList.splice(m, 1);
                break;
              }
            }
            /* const index = componentList.indexOf(component);
            if (index > -1) {
              componentList.splice(index, 1);
            } */
          }
        }
      }
    }

    return componentList;
  }
  const triggeredComponents = getTriggeredComponents();
  const triggeredComponentsExpanded = expandedLayers.has('triggeredComponents');
  const hasChildSettingsSelection = Boolean(selectedLayersNodeState?.hierarchy?.length);

  useEffect(() => {
    logSelectionDebug('selection-token-updated', {
      selectedFeatureId,
      selectedLayersNodeState,
      activeNodeName: activeNode?.name,
      activeNodePath: activeNode?.path,
    });
  }, [selectedFeatureId, selectedLayersNodeState, activeNode]);

  const getComponentAccentColor = (component) => {
    const rawCategory = Array.isArray(component?.iconCategory)
      ? component.iconCategory[0]
      : component?.iconCategory;
    return getIconCategoryColor(rawCategory) || '#789933';
  };

  const getNodeExpansionKey = (node) => node?.id || node?._layersExpansionKey || `${node?.path || 'unknown'}`;

  const attachExpansionKeys = (node, scopeKey, parentKey = '', featureScope = null) => {
    if (!node) return node;

    const resolvedFeatureScope = featureScope || node.id || 'unknown-feature';
    const localKeyPart = node.path || node.id || node.name || 'unknown';
    const nextKey = parentKey
      ? `${parentKey}>${localKeyPart}`
      : `${resolvedFeatureScope}||${scopeKey}::${localKeyPart}`;

    node._layersExpansionKey = nextKey;
    node._layersFeatureScope = resolvedFeatureScope;

    if (node.subNodes?.length) {
      node.subNodes.forEach((childNode) => attachExpansionKeys(childNode, scopeKey, nextKey, resolvedFeatureScope));
    }

    return node;
  };

  const collapseExpandedNodesExceptFeature = (featureScope) => {
    setPersistedExpandedNodes(prev => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        // Keep keys that belong to this feature (same id or child ids starting with featureScope)
        if (key === featureScope || key.startsWith(`${featureScope}-`) || key.startsWith(`${featureScope}||`)) {
          next[key] = value;
        }
      });
      return next;
    });
  };

  const setNodeExpansionState = (node, isExpanded) => {
    setPersistedExpandedNodes(prev => {
      const next = { ...prev };

      const updateBranch = (targetNode, expandedValue) => {
        if (!targetNode) return;
        next[getNodeExpansionKey(targetNode)] = expandedValue;
        if (!expandedValue && targetNode.subNodes?.length) {
          targetNode.subNodes.forEach(childNode => updateBranch(childNode, false));
        }
      };

      updateBranch(node, isExpanded);
      return next;
    });
  };

  const syncPersistedExpansionState = (node) => {
    if (!node) return node;
    const expansionKey = getNodeExpansionKey(node);
    if (Object.prototype.hasOwnProperty.call(persistedExpandedNodes, expansionKey)) {
      node.expanded = persistedExpandedNodes[expansionKey];
    }
    if (node.subNodes?.length) {
      node.subNodes.forEach(syncPersistedExpansionState);
    }
    return node;
  };

  const collapseNodeTree = (node) => {
    if (!node) return;
    node.expanded = false;
    if (node.subNodes?.length) {
      node.subNodes.forEach(collapseNodeTree);
    }
  };

  const collapseAllFeatureMenus = (featureNodeToKeepOpen = null) => {
    Object.values(leftListNewCustomisationItems || {}).forEach(collapseNodeTree);
    if (featureNodeToKeepOpen) {
      featureNodeToKeepOpen.expanded = true;
    }
    setPersistedExpandedNodes({});
    suppressNextActiveNodeExpansionRef.current = true;
  };

  useEffect(() => {
    const previousFeatureId = previousSelectedFeatureIdRef.current;
    const nextFeatureId = selectedFeatureId || null;
    const hasChildSettingsSelection = Boolean(selectedLayersNodeState?.hierarchy?.length);
    const selectedFeatureRootNode = nextFeatureId
      ? leftListNewCustomisationItems?.[nextFeatureId]
        || Object.values(leftListNewCustomisationItems || {}).find((node) => node?.id === nextFeatureId)
      : null;

    previousSelectedFeatureIdRef.current = nextFeatureId;

    if (!previousFeatureId || previousFeatureId === nextFeatureId) {
      return;
    }

    collapseAllFeatureMenus(selectedFeatureRootNode);

    setTimeout(() => {
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list');
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list_triggered_');
    }, 0);
  }, [selectedFeatureId, selectedLayersNodeState, leftListNewCustomisationItems]);

  // When activeNode changes, persist expansion of all its ancestors so they stay open
  const prevActiveNodeIdRef = useRef(null);
  useEffect(() => {
    const newId = activeNode?.id || null;
    if (newId === prevActiveNodeIdRef.current) return;
    prevActiveNodeIdRef.current = newId;
    if (!activeNode?.parent) return;

    if (suppressNextActiveNodeExpansionRef.current) {
      suppressNextActiveNodeExpansionRef.current = false;
      return;
    }

    const ancestors = [];
    let cur = activeNode.parent;
    while (cur) {
      ancestors.push(cur);
      cur = cur.parent;
    }
    if (!ancestors.length) return;

    setPersistedExpandedNodes(prev => {
      const next = { ...prev };
      ancestors.forEach(ancestor => {
        const key = getNodeExpansionKey(ancestor);
        next[key] = true;
        ancestor.expanded = true;
      });
      return next;
    });

    // Refresh NodeList so it picks up the new expanded states
    setTimeout(() => {
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list');
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list_triggered_');
    }, 0);
  }, [activeNode]);

  const selectTopLevelFeatureNode = (node, listId) => {
    if (!node || node.depth !== 0) return false;

    setSelectedLayersNodeState?.(buildNodeSelectionToken(node));

    if (selectedFeatureId !== node.id) {
      setSelectedFeatureId(node.id);
      setSelectedFeatureNode(node);

      const visualisedGroupNode = node.subNodes?.find(
        (subNode) => (subNode.isVisualised || subNode?.config?.groupSettings?.visualise)
          && subNode.config.visualisationConfig.startStep === 0
      );

      if (visualisedGroupNode) {
        activateNode(visualisedGroupNode);
        setTimelinePlayheadStep(visualisedGroupNode.config.visualisationConfig.startStep || 0);
      } else {
        clearActiveNode();
      }

      setTimelinePlayheadStep(0);
    }

    return true;
  };

  const expandAncestorsInPersistedState = (node) => {
    const ancestors = [];
    let current = node?.parent;
    while (current) {
      ancestors.push(current);
      current = current.parent;
    }
    if (!ancestors.length) return;
    setPersistedExpandedNodes(prev => {
      const next = { ...prev };
      ancestors.forEach(ancestor => {
        const key = getNodeExpansionKey(ancestor);
        next[key] = true;
        ancestor.expanded = true;
      });
      return next;
    });
  };

  const handleNodeExpandToggle = (node, expanded, listId) => {
    setNodeExpansionState(node, expanded);

    if (expanded && node.depth === 0) {
      collapseExpandedNodesExceptFeature(node._layersFeatureScope || node.id);
      selectTopLevelFeatureNode(node, listId);
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list');
      NODE_LIST_CONTROLLER.refreshNodeList('customisation-workspace-left-list_triggered_');
    }
  };

  const renderLayerNodeLabel = (component, nodeName, node) => {
    const labelClasses = ['layers-node-label'];
    const hasExpander = Boolean(node.hasChildren && node.enabled);
    const tokenMatch = doesSelectionTokenMatchNode(selectedLayersNodeState, node);
    const isCurrentSemanticNode = tokenMatch;
    const isAddNode = node.isAddButton || String(nodeName).trim().toLowerCase() === 'add new';
    const nodeLabelText = typeof nodeName === 'string' ? nodeName : String(nodeName ?? '');

    if (node.depth === 0) {
      labelClasses.push('layers-node-label--root');
    } else if (node.depth === 1) {
      labelClasses.push('layers-node-label--branch');
    } else {
      labelClasses.push('layers-node-label--leaf');
    }

    if (node.active) {
      labelClasses.push('layers-node-label--active');
    }

    if (isCurrentSemanticNode) {
      labelClasses.push('layers-node-label--current');
    }

    if (isAddNode) {
      labelClasses.push('layers-node-label--add');
    }

    if (node.openLegacyEditor || String(nodeName).toLowerCase().includes('legacy editor')) {
      labelClasses.push('layers-node-label--legacy');
    }

    const rowClasses = ['layers-node-row'];
    if (isCurrentSemanticNode) {
      rowClasses.push('layers-node-row--current');
    }

    return (
      <span className={rowClasses.join(' ')} style={{ '--layer-accent-color': getComponentAccentColor(component) }}>
        {node.depth > 0 && !hasExpander && <span className="layers-node-row__spacer" />}
        <span className={labelClasses.join(' ')}>
          {node.depth === 0 && (
            <span className={`layer-icon category-icon-lozenge category-icon-bg--${getIconCategoryClass(Array.isArray(component.iconCategory) ? component.iconCategory[0] : component.iconCategory)}`}>
              <LottieIcon icon={component.icon} varKey={component.varKey} size={20} />
            </span>
          )}
          {node.depth > 0 && !isAddNode && node.icon && node.icon !== 'add' && (
            <img className="v2-workspace__left-list-img-icon" alt='' src={WORKSPACE_LEFT_LIST_ICONS[node.icon]} />
          )}
          {isAddNode && <span className="layers-node-label__prefix">+</span>}
          <span className="layers-node-label__text" title={nodeLabelText}>{nodeName}</span>
        </span>
      </span>
    );
  };

  return (
    <div className="layers-panel">

      <div className="layers-header">
        <label className="stacking-toggle">
          <span className="toggle-label">Reorder Stacking</span>
          <div className="toggle-switch">
            <input 
              type="checkbox" 
              checked={isStackingMode}
              onChange={(e) => setIsStackingMode(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </div>
        </label>
        {isStackingMode && (
          <div className="stacking-hint">
            Drag to reorder z-index (bottom to top)
          </div>
        )}
        
        <label className="stacking-toggle" style={{ marginTop: '12px' }}>
          <span className="toggle-label">Show Expansion Lines</span>
          <div className="toggle-switch">
            <input 
              type="checkbox" 
              checked={showExpansionLines}
              onChange={(e) => setShowExpansionLines(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </div>
        </label>
      </div>

      {/* Flow selector below stacking order */}
      <div className="flow-selector-holder">
        <FlowSelector
          flows={flows}
          activeFlowId={activeFlowId}
          onFlowChange={onFlowChange}
          onCreateFlow={onCreateFlow}
        />
      </div>
      
      <div className={`layers-list ${draggedStackItem !== null ? 'is-dragging' : ''}`}>
        {!isStackingMode ? (
          // Normal hierarchical view
          layers.length === 0 ? (
            <div className="empty-layers">
              No layers added.
            </div>
          ) : (

            <>
            {layers.map((layer, index) => {
              if (layer.isStep) {
                const isExpanded = expandedLayers.has(layer.id);
                // Get all components that belong to this step (including expanded copies for expanded components)
                const childComponents = layers.filter(l => {
                  if (!l.isComponent) return false;
                  // Include components whose parent is this step
                  return l.parentStep === layer.id;
                });
                
                return (
                  <div key={layer.id} className="layer-item">
                    <div 
                      className="layer-content step-layer" 
                      onClick={() => onLayerToggle(layer.id)}
                    >
                      {childComponents.length > 0 && (
                        <span className={`layer-dropdown-arrow ${isExpanded ? 'expanded' : ''}`}>
                          ▶
                        </span>
                      )}
                      <span className="layer-step-badge">{layer.sequenceLabel || '--'}</span>
                      <span className="layer-name">{layer.name}</span>
                    </div>
                    {isExpanded && childComponents.length > 0 && (
                      <div className="layer-nested">
                        {childComponents.map((component) => {
                          const listId = 'customisation-workspace-left-list_' + component.featureId + '_' + component.id + (component.copyId ? ('_' + component.copyId) : '');
                          const listNode = leftListNewCustomisationItems?.[component.featureId]
                            || (leftListNewCustomisationItems && component.varKey
                                ? Object.values(leftListNewCustomisationItems).find(n => n.varKey === component.varKey)
                                : null);
                          if (!listNode) return null;
                          attachExpansionKeys(listNode, listId);
                          syncPersistedExpansionState(listNode);
                          // Patch the stale featureId so future lookups on the same object hit the fast path
                          if (listNode.id && component.featureId !== listNode.id) {
                            component.featureId = listNode.id;
                          }
                          return (
                            <Fragment key={component.id + (component.copyId ? ('_' + component.copyId) : '')}>
                              {/* <div key={component.copyId || component.id} className={`layer-child ${component.isExpandedCopy ? 'expanded-copy' : ''}`}> */}
                              {/* <span className="layer-icon">{component.icon}</span>
                              <span className="layer-name">{component.name}</span> */}
                                          <NodeList
                                              id={listId}
                                              nodes={[listNode] || []}
                  
                                              indentation='1.2rem'

                                              clickAnywhereToExpand={false}
                                              onNodeExpandToggle={(node, expanded) => handleNodeExpandToggle(node, expanded, listId)}
                  
                                              expandNodeElement={<span style={{ fontSize: '10px', color: '#6c757d', width: '12px', textAlign: 'center', flexShrink: 0, transition: 'transform 0.2s ease', display: 'inline-block', transform: 'rotate(0deg)' }}>▶</span>}
                                              collapseNodeElement={<span style={{ fontSize: '10px', color: '#6c757d', width: '12px', textAlign: 'center', flexShrink: 0, transition: 'transform 0.2s ease', display: 'inline-block', transform: 'rotate(90deg)' }}>▶</span>}
                  
                                              renderNodeName={(nodeName, node, nodes, isExpandable) => {
                                                  if (node.depth === 0) {
                                                    return renderLayerNodeLabel(component, nodeName, node);
                                                  }
                                                  return renderLayerNodeLabel(component, nodeName, node);
                                              }}
                                              getSubNodes={getSubNodes}

                                              checkNodeVisibility={
                                                (node, nodes) => {
                                                  // console.log("Checking visibility for node: ", node, nodes);
                                                  if (node.depth === 0) {
                                                    return true; // Always show top-level nodes (steps)
                                                  }

                                                  return evaluateDisplayConditionsForListNode(node, campaignData);
                                                }
                                              }
                  
                                              onNodeClick={(e, node, nodes) => {
                                                  // console.log("Node clicked:", node);
                                                  if (node.openLegacyEditor) {
                                                    activateNode(node);
                                                  } else
                                                  if (node.openUrl) {
                                                      navigate(node.urlToOpen);
                                                  } else
                                                  if (node.isAddButton) {
                                                      // console.log("Add button clicked - add new node:", node);
                                                      addVal(node.valsArray, node.varSchema.valSchema, node.featureData.feature, null, node);
                                                  } else
                                                  if (node.depth === 0) {
                                                      setHoveredStackingItem?.(null);
                                                      const nextSelectionToken = buildNodeSelectionToken(node);
                                                      const isSwitchingFeature = selectedFeatureId !== node.id;
                                                      logSelectionDebug('top-node-click', {
                                                        clickedName: node?.name,
                                                        clickedPath: node?.path,
                                                        selectedFeatureId,
                                                        isSwitchingFeature,
                                                        nextSelectionToken,
                                                      });
                                                      setSelectedLayersNodeState?.(nextSelectionToken);
                                                      if (node.hasChildren && node.subNodes && node.subNodes.length > 0) {
                                                          if (isSwitchingFeature) {
                                                            collapseAllFeatureMenus(node);
                                                          }
                                                          node.expanded = isSwitchingFeature ? true : !node.expanded;
                                                          setNodeExpansionState(node, node.expanded);
                                                          NODE_LIST_CONTROLLER.refreshNodeList(listId);
                                                      }
                                                        selectTopLevelFeatureNode(node, listId);
                                                      // Return true to select this node
                                                      return true;
                                                  } else if (node.topNode) {
                                                      setHoveredStackingItem?.(null);
                                                        const nextSelectionToken = buildNodeSelectionToken(node, node.topNode.id);
                                                        logSelectionDebug('child-node-click', {
                                                        clickedName: node?.name,
                                                        clickedPath: node?.path,
                                                        topNodeId: node?.topNode?.id,
                                                        selectedFeatureId,
                                                        nextSelectionToken,
                                                        });
                                                        setSelectedLayersNodeState?.(nextSelectionToken);
                                                      if (selectedFeatureId !== node.topNode.id) {
                                                          setSelectedFeatureId(node.topNode.id);
                                                          setSelectedFeatureNode(node.topNode);
                                                      }
                                                      if (!node.active) {
                                                          expandAncestorsInPersistedState(node);
                                                          activateNode(node);
                                                      }
                                                      return true;
                                                  }
                                                  forceUpdateComponentFunc();
                                                  return false;
                                              }}
                  
                                              enableRightClickMenu={true}
                                              onNodeRightClick={(e, node, nodes, onDone) => {
                                                  return nodeListRightClicked(e, node, nodes, onDone);
                                              }}

                                              className={
                                                selectedFeatureId === listNode?.id 
                                                  ? (hasChildSettingsSelection ? 'active-feature-node-list' : 'selected-feature-node')
                                                  : (selectedFeatureId && selectedFeatureId !== listNode?.id ? 'unselected-node-list' : null)
                                              }
                                          />
                            {/* </div> */}
                            </Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return null; // Component layers are rendered as children of steps
            })}

              {triggeredComponents.length > 0 && (
                <div className="triggered-components">
                  <div key={'triggeredComponents'} className="layer-item">
                    <div 
                      className="layer-content step-layer" 
                      onClick={() => onLayerToggle('triggeredComponents')}
                    >

                        <span className={`layer-dropdown-arrow ${triggeredComponentsExpanded ? 'expanded' : ''}`}>
                          ▶
                        </span>
                      
                      {/* <span className={`layer-icon category-icon-lozenge category-icon-bg--${getIconCategoryClass(Array.isArray(layer.iconCategory) ? layer.iconCategory[0] : layer.iconCategory)}`}><LottieIcon icon={layer.icon} varKey={layer.varKey} size={24} /></span> */}
                      <span className="layer-name">Triggered Components</span>
                    </div>
                    {triggeredComponentsExpanded && (
                      <div className="layer-nested">
                        {triggeredComponents.map((component) => {
                          const listId = 'customisation-workspace-left-list_triggered_' + component.id;
                          const listNode = leftListNewCustomisationItems?.[component.id] ?? null;
                          if (!listNode) return null;
                          attachExpansionKeys(listNode, listId);
                          syncPersistedExpansionState(listNode);

                          return (
                            <NodeList
                                key={'triggered_' + component.id}

                                id={listId}
                                nodes={[listNode] || []}
    
                                indentation='1.2rem'

                                clickAnywhereToExpand={false}
                                onNodeExpandToggle={(node, expanded) => handleNodeExpandToggle(node, expanded, listId)}
    
                                expandNodeElement={<span style={{ fontSize: '10px', color: '#6c757d', width: '12px', textAlign: 'center', flexShrink: 0, transition: 'transform 0.2s ease', display: 'inline-block', transform: 'rotate(0deg)' }}>▶</span>}
                                collapseNodeElement={<span style={{ fontSize: '10px', color: '#6c757d', width: '12px', textAlign: 'center', flexShrink: 0, transition: 'transform 0.2s ease', display: 'inline-block', transform: 'rotate(90deg)' }}>▶</span>}
    
                                renderNodeName={(nodeName, node, nodes, isExpandable) => {
                                    if (node.depth === 0) {
                                    return renderLayerNodeLabel(component, nodeName, node);
                                    }
                                  return renderLayerNodeLabel(component, nodeName, node);
                                }}
                                getSubNodes={getSubNodes}

                                checkNodeVisibility={
                                  (node, nodes) => {
                                    // console.log("Checking visibility for node: ", node, nodes);
                                    if (node.depth === 0) {
                                      return true; // Always show top-level nodes (steps)
                                    }

                                    return evaluateDisplayConditionsForListNode(node, campaignData);
                                  }
                                }
    
                                onNodeClick={(e, node, nodes) => {
                                    // console.log("Node clicked:", node);
                                    if (node.openLegacyEditor) {
                                      activateNode(node);
                                    } else
                                    if (node.openUrl) {
                                        navigate(node.urlToOpen);
                                    } else
                                    if (node.isAddButton) {
                                        // console.log("Add button clicked - add new node:", node);
                                        addVal(node.valsArray, node.varSchema.valSchema, node.featureData.feature, null, node);
                                    } else
                                    if (node.depth === 0) {
                                      setHoveredStackingItem?.(null);
                                        const nextSelectionToken = buildNodeSelectionToken(node);
                                        const isSwitchingFeature = selectedFeatureId !== node.id;
                                        logSelectionDebug('top-node-click-triggered', {
                                          clickedName: node?.name,
                                          clickedPath: node?.path,
                                          selectedFeatureId,
                                          isSwitchingFeature,
                                          nextSelectionToken,
                                        });
                                        setSelectedLayersNodeState?.(nextSelectionToken);
                                        if (node.hasChildren && node.subNodes && node.subNodes.length > 0) {
                                            if (isSwitchingFeature) {
                                              collapseAllFeatureMenus(node);
                                            }
                                            node.expanded = isSwitchingFeature ? true : !node.expanded;
                                          setNodeExpansionState(node, node.expanded);
                                            NODE_LIST_CONTROLLER.refreshNodeList(listId);
                                        }
                                        selectTopLevelFeatureNode(node, listId);
                                        // Return true to select this node
                                        return true;
                                    } else if (node.topNode) {
                                      setHoveredStackingItem?.(null);
                                        const nextSelectionToken = buildNodeSelectionToken(node, node.topNode.id);
                                        logSelectionDebug('child-node-click-triggered', {
                                          clickedName: node?.name,
                                          clickedPath: node?.path,
                                          topNodeId: node?.topNode?.id,
                                          selectedFeatureId,
                                          nextSelectionToken,
                                        });
                                        setSelectedLayersNodeState?.(nextSelectionToken);
                                        if (selectedFeatureId !== node.topNode.id) {
                                            setSelectedFeatureId(node.topNode.id);
                                            setSelectedFeatureNode(node.topNode);
                                        }
                                        if (!node.active) {
                                            expandAncestorsInPersistedState(node);
                                            activateNode(node);
                                        }
                                      return true;
                                    }
                                    forceUpdateComponentFunc();
                                    return false;
                                }}
    
                                enableRightClickMenu={true}
                                onNodeRightClick={(e, node, nodes, onDone) => {
                                    return nodeListRightClicked(e, node, nodes, onDone);
                                }}

                                className={
                                  selectedFeatureId === listNode?.id 
                                    ? (hasChildSettingsSelection ? 'active-feature-node-list' : 'selected-feature-node')
                                    : (selectedFeatureId && selectedFeatureId !== listNode?.id ? 'unselected-node-list' : null)
                                }
                            />
                          )
                        })}

                      </div>
                    )}
                  </div>
                </div>
              )}

            </>
          )
        ) : (
          // Stacking order view (flat list)
          stackingOrder.length === 0 ? (
            <div className="empty-layers">
              No components to stack.
            </div>
          ) : (
            <>
              {stackingOrder.map((item, index) => {
                const isDragging = draggedStackItem === index;
                const showDropIndicator = !isDragging && dragOverStackItem === index && draggedStackItem !== null;
                
                const handleDragStartSimple = (e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  onStackDragStart(index, item.id);
                };
                
                const handleDrop = (e) => {
                  e.preventDefault();
                  // Immediately hide the drag ghost
                  e.dataTransfer.dropEffect = 'none';
                };
                
                return (
                  <React.Fragment key={item.id}>
                    {showDropIndicator && (
                      <div className="stacking-drop-indicator">
                        <div className="drop-line"></div>
                      </div>
                    )}
                    <div 
                      className={`stacking-item ${isDragging ? 'dragging' : ''}`}
                      draggable={true}
                      onDragStart={handleDragStartSimple}
                      onDragEnd={onStackDragEnd}
                      onDragOver={(e) => onStackDragOver(e, index)}
                      onDrop={handleDrop}
                      onMouseEnter={() => {
                        const targetId = item.featureId || item.id;
                        setHoveredStackingItem(targetId);
                      }}
                      onMouseLeave={() => setHoveredStackingItem(null)}
                    >
                      <div className="stacking-drag-handle">⋮⋮</div>
                      <span className="stacking-icon"><LottieIcon icon={item.icon} varKey={item.varKey} size={20} /></span>
                      <span className="stacking-name">{item.name}</span>
                    </div>
                  </React.Fragment>
                );
              })}
              {/* Drop zone at the end of the list */}
              {draggedStackItem !== null && (
                <div 
                  className="stacking-drop-zone-end"
                  onDragOver={(e) => onStackDragOver(e, stackingOrder.length)}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'none';
                  }}
                >
                  {dragOverStackItem === stackingOrder.length && (
                    <div className="stacking-drop-indicator">
                      <div className="drop-line"></div>
                    </div>
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

export default LayersPanel;
